import type { FetchRequestError } from "@lib/errors/FetchRequestError";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type {
  SHARED_CONSTANTS_FEATURE_FLAG,
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES,
} from "@lib/shared_constants.json";

import type { AudienceDescriptionHash } from "../canvas/common";
import type { CampaignFilter, CreatedEditedAt, SegmentsData } from "../campaigns";
import type { EngagementSegmentFilterResult } from "../engagementSegmentData";

export interface CoreRolloutFeatureFlagProps {
  feature_flag_id: string;
  feature_flag_send_percentage: number;
  feature_flag_properties: string;
  feature_flag_enabled: boolean;
}
export interface CoreRolloutFeatureFlagResult extends CoreRolloutFeatureFlagProps {
  rollout_id: string;
  rollout_nst: string;
  feature_flag_name: string;
  feature_flag_description: string;
}

export interface RolloutFeatureFlagResult {
  id: string;
  app_group_id: string;
  segments_data_array: SegmentsData[][];
  filters: any[];
  core_rollout: CoreRolloutFeatureFlagResult;
  is_archived: boolean;
  created_by: string;
  last_edited: string;
  last_edited_by_id: string;
  tag_names: string[];
  territory_ids: any[];
}

export interface EngagementFeatureFlagRolloutUpsertResult {
  info: string;
  rollout: RolloutFeatureFlagResult;
}

export interface MultiRolloutsFeatureFlagProps {
  id?: string;
  feature_flag_send_percentage: number;
  segment_ids: [string][];
  segments_data_array?: SegmentsData[][];
  filters?: any;
  filters_unchanged?: boolean;
  exclusion_filters?: EngagementSegmentFilterResult;
  using_v2_filters?: boolean;
  audience_description_hash?: AudienceDescriptionHash;
  is_new_rule?: boolean;
}

export interface MultiRolloutFeatureFlagV2FiltersProps extends MultiRolloutsFeatureFlagProps {
  filters?: EngagementSegmentFilterResult;
  exclusion_filters?: EngagementSegmentFilterResult;
  using_v2_filters: true;
}

export interface MultiRolloutFeatureFlagLegacyFiltersProps extends MultiRolloutsFeatureFlagProps {
  filters: EngagementSegmentFilterResult[];
  using_v2_filters: false;
}

export interface PostEngagementFeatureFlagRolloutUpsertProps {
  app_group_id: string;
  rollout_id: string;
  rollout_name: string;
  rollout_description: string;
  segment_ids: [string][];
  filters: any[];
  core_rollout: CoreRolloutFeatureFlagProps;
  multi_rollouts: MultiRolloutsFeatureFlagProps[];
}

export interface FeatureFlagData {
  feature_flag_name: string;
  feature_flag_description: string;
  app_group_id: string;
  id: string;
  feature_flag_enabled: boolean;
  feature_flag_id: string;
  feature_flag_properties: string;
  feature_flag_send_percentage: number;
  segments_data_array: SegmentsData[][];
  filters: CampaignFilter[];
  last_edited: CreatedEditedAt | string;
  is_archived: boolean;
  tag_names: string[];
  territory_ids: any[];
  api_campaign_id?: string;
  variation_id?: string;
}

export type FeatureFlagPropertyTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_TOO_LONG"]
>;

export type FeatureFlagPropertyNotJsonError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_NOT_JSON"]
>;

export type FeatureFlagPropertyMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_MISSING"]
>;

export type FeatureFlagPropertyTypeMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_TYPE_MISSING"]
>;

export type FeatureFlagPropertyKeyBlankError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_KEY_BLANK"]
>;

export type FeatureFlagPropertyStructureInvalidError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_STRUCTURE_INVALID"]
>;

export type FeatureFlagPropertyTypeUnknownError = FetchUserEncodedError<
  SHARED_CONSTANTS_SDK_MESSAGE_PROPERTIES["VALIDATION_ERRORS"]["PROPERTIES_TYPE_UNKNOWN"]
>;

export type FeatureFlagPropertyError =
  | FeatureFlagPropertyTooLongError
  | FeatureFlagPropertyNotJsonError
  | FeatureFlagPropertyMissingError
  | FeatureFlagPropertyTypeMissingError
  | FeatureFlagPropertyKeyBlankError
  | FeatureFlagPropertyStructureInvalidError
  | FeatureFlagPropertyTypeUnknownError;

export type FeatureFlagUpsertDescriptionTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["DESCRIPTION_TOO_LONG"]
>;

export type FeatureFlagUpsertIdMustBeUniqueError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["ID_MUST_BE_UNIQUE"]
>;

export type FeatureFlagUpsertIdTooShortError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["ID_TOO_SHORT"]
>;

export type FeatureFlagUpsertIdTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["ID_TOO_LONG"]
>;

export type FeatureFlagUpsertNameMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["NAME_MISSING"]
>;

export type FeatureFlagUpsertNameTooShortError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["NAME_TOO_SHORT"]
>;

export type FeatureFlagUpsertNameTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["NAME_TOO_LONG"]
>;

export type FeatureFlagUpsertNoIdPresentError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["NO_ID_PRESENT"]
>;

export type FeatureFlagUpsertParentCampaignIdInvalidError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["PARENT_CAMPAIGN_ID_INVALID_ERROR"]
>;

export type FeatureFlagUpsertParentRolloutIdInvalidError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["PARENT_ROLLOUT_ID_INVALID_ERROR"]
>;

export type FeatureFlagUpsertSendPercentageNonzeroYetNoTargetError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["SEND_PERCENTAGE_NONZERO_YET_NO_TARGET"]
>;

export type FeatureFlagUpsertSendPercentageNotPresentError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["SEND_PERCENTAGE_NOT_PRESENT"]
>;

export type FeatureFlagUpsertSendPercentageOutOfBoundsError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["SEND_PERCENTAGE_OUT_OF_BOUNDS"]
>;

export type FeatureFlagUpsertStepOverrideEnabledMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["STEP_OVERRIDE_ENABLED_MISSING_ERROR"]
>;

export type FeatureFlagUpsertStepParentNstMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["STEP_PARENT_NST_MISSING_ERROR"]
>;

export type FeatureFlagUpsertStepRolloutIdMissingError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["STEP_ROLLOUT_ID_MISSING_ERROR"]
>;

export type FeatureFlagUpsertTooManyMultiRolloutsError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["TOO_MANY_MULTI_ROLLOUTS"]
>;

export type FeatureFlagUpsertInvalidMultiRolloutFiltersError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["INVALID_MULTI_ROLLOUT_FILTERS"]
>;

export type FeatureFlagUpsertInvalidMultiRolloutSegmentsError = FetchUserEncodedError<
  SHARED_CONSTANTS_FEATURE_FLAG["VALIDATION_ERRORS"]["INVALID_MULTI_ROLLOUT_SEGMENTS"]
>;

export type FeatureFlagUpsertError =
  | FeatureFlagUpsertDescriptionTooLongError
  | FeatureFlagUpsertIdMustBeUniqueError
  | FeatureFlagUpsertIdTooShortError
  | FeatureFlagUpsertIdTooLongError
  | FeatureFlagUpsertNameMissingError
  | FeatureFlagUpsertNameTooShortError
  | FeatureFlagUpsertNameTooLongError
  | FeatureFlagUpsertNoIdPresentError
  | FeatureFlagUpsertParentCampaignIdInvalidError
  | FeatureFlagUpsertParentRolloutIdInvalidError
  | FeatureFlagUpsertSendPercentageNonzeroYetNoTargetError
  | FeatureFlagUpsertSendPercentageNotPresentError
  | FeatureFlagUpsertSendPercentageOutOfBoundsError
  | FeatureFlagUpsertStepOverrideEnabledMissingError
  | FeatureFlagUpsertStepParentNstMissingError
  | FeatureFlagUpsertStepRolloutIdMissingError
  | FeatureFlagUpsertTooManyMultiRolloutsError
  | FeatureFlagUpsertInvalidMultiRolloutFiltersError
  | FeatureFlagUpsertInvalidMultiRolloutSegmentsError
  | FeatureFlagPropertyError;

export const isFeatureFlagUpsertError = (error: FetchRequestError): error is FeatureFlagUpsertError => {
  return error instanceof FetchUserEncodedError;
};

export const isFeatureFlagUniqueIdError = (
  error: FetchRequestError
): error is FeatureFlagUpsertIdMustBeUniqueError => {
  return error instanceof FetchUserEncodedError && error.errorCode === "id_must_be_unique";
};
